import { useCallback, useEffect, useMemo, useState, startTransition } from "react";
import { useRouter } from "next/router";
import { useDispatch } from "react-redux";
import LoginCard from "@/components/Login/LoginCard";
import Box from "@mui/material/Box";
import PageHead from "@/components/PageHead";
import { useI18n } from "@/i18n/I18nProvider";
import { EnvKey } from "../../utils/const";
import { api } from "@/utils/Axios";
import { userAction } from "@/Redux/store";
import { FetchUserData } from "@/utils/Fetch_userDataFn";

function Login({ setauthenticated }) {
  const router = useRouter();
  const { t } = useI18n();

  const dispatch = useDispatch();

  const [Phone, setPhone] = useState("");
  const [Password, setPassword] = useState("");

  const [PhoneError, setPhoneError] = useState(false);
  const [PasswordError, setPasswordError] = useState(false);

  const [resetPasswordVisible, setResetPasswordVisible] = useState(false);

  const [errorMsg, setErrorMsg] = useState("");
  const [Next, SetNext] = useState(true);

  // Avoid CLS/LCP issues: wait until we check localStorage on client
  const [authChecked, setAuthChecked] = useState(false);
  const [isLogin, setIsLogin] = useState(false);

  // these existed before (kept to avoid breaking side-effects)
  const [Loading, SetLoading] = useState(true);
  const [data, SetData] = useState("");

  useEffect(() => {
    // client-only auth check
    try {
      const token = localStorage.getItem("UT_UI");
      const loggedIn = !!token;

      setIsLogin(loggedIn);
      setAuthChecked(true);

      if (loggedIn) {
        // replace to avoid back navigation to login
        router.replace("/contract");
      }
    } catch {
      setIsLogin(false);
      setAuthChecked(true);
    }
  }, [router]);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      // prevent double submit
      if (!Next) return;

      if (!/^\d{6}$/.test(Password)) {
        setPasswordError(true);
        setErrorMsg(t.Login.ErrorMessPass);
        return;
      }

      setPasswordError(false);
      SetNext(false);
      setErrorMsg("");

      try {
        const Body = {
          phone: `+2${Phone}`,
          password: Password,
        };

        const response = await api.post(`/auth/serviceLogin`, Body);


        if (response.status === 200 && response.data) {
          const userId = response.data.userId;

          // dispatch minimal critical data first
          dispatch(userAction.update({ key: "id", val: userId }));

          // wallet fetch should not block navigation (performance)
          if (response.data?.nationalId) {
            void api
              .get(`/wallets/${userId}`)
              .then((res) => {
                dispatch(userAction.update({ key: "wallet", val: res.data }));
              })
              .catch(() => {});
          }

          // IMPORTANT PERF: dynamic import jsonwebtoken only on submit success
          const jwtModule = await import("jsonwebtoken");
          const signFn = jwtModule?.default?.sign || jwtModule?.sign;

          const userData = signFn(
            {
              token: response.data.tokens.accessToken,
              refreshToken: response.data.tokens.refreshToken,
              phone: Body.phone,
              id: userId,
            },
            EnvKey.secretKey,
          );

          localStorage.setItem("UT_UI", userData);

          // Navigate ASAP (don’t wait for extra requests)
          startTransition(() => {
            router.replace("/contract");
          });

          // fire-and-forget user data fetch (don’t block navigation)
          void FetchUserData(userId, SetLoading, SetData);
        } else {
          setErrorMsg(t.Login.ErrorMessPass);
        }
      } catch (error) {
        setErrorMsg(t.Login.ErrorMessPass);

        const status = error?.response?.data?.statusCode;
        if (status === 403) {
          setErrorMsg(
            error?.response?.data?.message ||
              "try agin later account is Locked",
          );
        }
        if (status === 500) {
          setErrorMsg(t.Login.ErrorMessPass);
        }
      } finally {
        SetNext(true);
      }
    },
    [Next, Phone, Password, dispatch, router, t],
  );

  const containerSx = useMemo(
    () => ({
      backgroundColor: "#EBEBED",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "74vh",
      position: "relative",
    }),
    [],
  );

  // Stable wrapper to reduce CLS when switching between Login & OTP
  const contentWrapperSx = useMemo(
    () => ({
      width: { xs: "100%", sm: 488 },
      minHeight: 520,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      px: { xs: 2, sm: 0 },
    }),
    [],
  );

  return (
    <>
      <PageHead
        title={t?.pages_main_tags?.login?.title}
        description={t?.pages_main_tags?.login?.meta_desc}
      />

      {/* Until auth is checked, show a stable skeleton to avoid CLS and improve perceived LCP */}
      {!authChecked ? (
        <Box sx={containerSx}>
          <Box sx={contentWrapperSx}>
            <div className="box-loading">
              <span className="loader"></span>
            </div>
          </Box>
        </Box>
      ) : (
        !isLogin && (
          <Box sx={containerSx}>
            <Box sx={contentWrapperSx}>
              <LoginCard
                Phone={Phone}
                setPhone={setPhone}
                PhoneError={PhoneError}
                setPhoneError={setPhoneError}
                Password={Password}
                setPassword={setPassword}
                PasswordError={PasswordError}
                errorMsg={errorMsg}
                setErrorMsg={setErrorMsg}
                handleSubmit={handleSubmit}
                Title2={t.Login.PassTitle}
                Label={t.Login.PassLabel}
                resetPasswordVisible={resetPasswordVisible}
                setResetPasswordVisible={setResetPasswordVisible}
                type={"password"}
                Next={Next}
              />
            </Box>
          </Box>
        )
      )}
    </>
  );
}

export default Login;
